import { useCallback, useEffect, useRef, useState } from 'react';

/* useDictation — J.O.'s side of the voice interface, slice 1.
 *
 * Push-to-talk into the composer using the browser's own speech
 * recognition (Chrome, Edge, Safari; nothing on Firefox, where the mic
 * button simply does not render). Final phrases are handed to `onText`
 * as they land, so the words appear in the box while he is still talking
 * and he sends them like anything typed. Nothing is recorded or uploaded
 * by this app; the browser's recognizer does the listening. Milli's
 * spoken side (text to speech) is the next slice. */

const Recognizer = () =>
  (typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition)) || null;

const LANGS = { en: 'en-US', es: 'es-US' };

export default function useDictation({ onText, language = 'en' } = {}) {
  const supported = Boolean(Recognizer());
  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState('');
  const recRef = useRef(null);
  const onTextRef = useRef(onText);
  useEffect(() => {
    onTextRef.current = onText;
  }, [onText]);

  const stop = useCallback(() => {
    try {
      recRef.current?.stop();
    } catch {
      /* already stopped */
    }
    recRef.current = null;
    setListening(false);
    setInterim('');
  }, []);

  const start = useCallback(() => {
    const R = Recognizer();
    if (!R || recRef.current) return;
    const rec = new R();
    rec.lang = LANGS[String(language).slice(0, 2)] || LANGS.en;
    rec.continuous = true;
    rec.interimResults = true;
    rec.onresult = (e) => {
      let partial = '';
      for (let i = e.resultIndex; i < e.results.length; i += 1) {
        const r = e.results[i];
        const text = r[0]?.transcript ?? '';
        if (r.isFinal) onTextRef.current?.(text.trim());
        else partial += text;
      }
      setInterim(partial.trim());
    };
    rec.onerror = () => stop();
    rec.onend = () => {
      // The recognizer ends itself after silence; reflect that in the button.
      recRef.current = null;
      setListening(false);
      setInterim('');
    };
    recRef.current = rec;
    try {
      rec.start();
      setListening(true);
    } catch {
      recRef.current = null;
    }
  }, [language, stop]);

  const toggle = useCallback(() => {
    if (listening) stop();
    else start();
  }, [listening, start, stop]);

  // Leaving the page stops the mic.
  useEffect(() => () => stop(), [stop]);

  return { supported, listening, interim, start, stop, toggle };
}
